'use client';

import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { authApi } from '@/lib/api';

/**
 * Gate verifikasi email full-screen yang memblokir seluruh aplikasi sampai user
 * memasukkan kode OTP 6 digit yang dikirim ke emailnya. Tidak bisa di-dismiss —
 * satu-satunya jalan keluar adalah verifikasi atau logout.
 */
const RESEND_COOLDOWN = 60; // detik

export default function EmailOtpGateModal() {
  const { user, refreshUser, logout } = useAuth();
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [sentOnce, setSentOnce] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const autoSent = useRef(false);

  const sendCode = async () => {
    if (sending || cooldown > 0) return;
    setSending(true);
    setError(null);
    try {
      await authApi.emailOtp.send();
    } catch {
      // Pesan generic — jangan bocorkan info. User tetap diminta cek email.
    } finally {
      setSending(false);
      setSentOnce(true);
      setCooldown(RESEND_COOLDOWN);
    }
  };

  // Kirim kode otomatis sekali saat gate pertama muncul.
  useEffect(() => {
    if (autoSent.current) return;
    autoSent.current = true;
    void sendCode();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Hitung mundur cooldown "kirim ulang".
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      const res = await authApi.emailOtp.verify(code.trim());
      if (res.status) {
        await refreshUser();
      } else {
        setError(res.message || 'Kode tidak valid.');
      }
    } catch (err: any) {
      setError(err?.message || 'Kode tidak valid. Coba lagi.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/70 backdrop-blur-sm p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Verifikasi email"
        className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl"
      >
        <div className="flex flex-col items-center text-center">
          <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-blue-50">
            <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <h2 className="text-lg font-bold text-gray-900">Verifikasi Email</h2>
          <p className="mt-1 text-sm text-gray-500">
            Kami mengirim kode 6 digit ke{' '}
            <span className="font-medium text-gray-700">{user?.email}</span>. Masukkan kode
            untuk memastikan email aktif digunakan.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="mt-5 space-y-4">
          <input
            autoFocus
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(e) => { setCode(e.target.value.replace(/\D/g, '')); setError(null); }}
            placeholder="000000"
            className="w-full rounded-xl border border-gray-300 px-4 py-3 text-center text-2xl font-semibold tracking-[0.5em] text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
          />

          {error && <p className="text-center text-sm text-red-600">{error}</p>}

          <button
            type="submit"
            disabled={submitting || code.length < 6}
            className="w-full rounded-xl bg-blue-600 px-4 py-3 font-semibold text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting ? 'Memverifikasi…' : 'Verifikasi'}
          </button>
        </form>

        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={() => void sendCode()}
            disabled={sending || cooldown > 0}
            className="text-xs text-blue-600 transition-colors hover:text-blue-700 disabled:opacity-50"
          >
            {sending
              ? 'Mengirim…'
              : cooldown > 0
                ? `Kirim ulang kode (${cooldown}s)`
                : sentOnce
                  ? 'Tidak menerima kode? Kirim ulang'
                  : 'Kirim kode'}
          </button>
        </div>

        <div className="mt-3 text-center">
          <button
            type="button"
            onClick={() => { void logout(); }}
            className="text-xs text-gray-400 transition-colors hover:text-gray-600"
          >
            Keluar dari akun {user?.email ? `(${user.email})` : ''}
          </button>
        </div>
      </div>
    </div>
  );
}
